import {
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';
import {I18n} from 'aws-amplify/utils';

import * as CertificatesService from '../services/CertificatesService';
import {classifyRequestError} from '../services/requestErrors';
import {certificatesKeys} from './useCertificatesQuery';
import {
    certificateWriteErrorCopy,
    deleteRefusedCopy,
} from '../writeErrorMessages';
import {
    Certificate,
    certificateStatusCopy,
    DeleteCertificateInput,
    DeleteCertificateResult,
    RequestCertificateInput,
} from '../types';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../../../components/ServiceNotifications';

// FNP-1193 — THE CERTIFICATE WRITE HOOKS.
//
// ⚠️ TWO MUTATIONS, AND THEY FAIL IN OPPOSITE SHAPES. This is the single most
// important thing to know before reading either hook.
//
//   requestCertificate  THROWS on refusal. A rejected promise.
//   deleteCertificate   RESOLVES on refusal, with `deleted: false`.
//
// That is not an inconsistency to be smoothed over — it is the schema saying
// two different things. A request that is refused did not happen. A delete that
// is refused DID happen: the provider was asked, it answered, and its answer
// ("this certificate is serving something") is a useful fact rather than an
// error. Flattening the second into the first would throw that away.
//
// The practical consequence: `onSuccess` for the delete must NOT assume the
// certificate is gone. It has to read `deleted`.

const notifyRequestError = (e: unknown) => {
    const error = classifyRequestError(e);
    const copy = certificateWriteErrorCopy(error.kind);
    // ⚠️ The provider's sentence is APPENDED, never substituted for. A
    // re-worded provider message is what made FNP-1087 take a whole
    // investigation.
    const detail = error.providerMessage
        ? `${copy.action} — ${error.providerMessage}`
        : copy.action;
    showErrorNotification(`${copy.title}. ${detail}`);
};

/**
 * Request a certificate.
 *
 * ⚠️ A RETURNED CERTIFICATE IS NOT AN ISSUED ONE, and the notification must
 * never imply it is.
 *
 * ACM issues nothing synchronously. The certificate comes back
 * PENDING_VALIDATION at best, and it can come back VALIDATION_NOT_WRITTEN — the
 * certificate exists at the provider but Flexi did not manage to publish the
 * validation record, which is a real and different problem that needs the
 * operator's attention immediately rather than after a 30-minute threshold.
 *
 * So the success notification REPORTS THE STATE IT ACTUALLY GOT rather than
 * saying "requested" and leaving the operator to discover the rest.
 */
export const useRequestCertificateMutation = () => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: RequestCertificateInput): Promise<Certificate | undefined> =>
            CertificatesService.requestNewCertificate(input),
        onSuccess: (certificate) => {

            // The schema allows a null result, and it means the request
            // reported nothing. Inventing a certificate for it would be exactly
            // the fabrication the nullable return exists to avoid.
            if (!certificate) {
                showSuccessNotification(
                    I18n.get('The request was accepted but the API did not report the certificate. Reload the list to see the current state.'),
                );
                queryClient.invalidateQueries({queryKey: certificatesKeys.lists()});
                return;
            }

            const copy = certificateStatusCopy(certificate.status, I18n.get);

            showSuccessNotification(
                `${I18n.get('Certificate requested')} — ${copy.label}. ${copy.detail}`,
            );

            queryClient.invalidateQueries({queryKey: certificatesKeys.lists()});
        },
        onError: (e: unknown) => {
            notifyRequestError(e);
        },
    });
};

/**
 * Delete a certificate.
 *
 * ⚠️ THE PROVIDER IS THE AUTHORITY, AND A REFUSAL RESOLVES RATHER THAN THROWS.
 *
 * `deleteCertificate` returns a `DeleteCertificateResult` whose `deleted` field
 * is the answer. An in-use refusal comes back with `deleted: false`,
 * `inUse: true` and the provider's own message — through the SUCCESS path.
 *
 * So this hook's `onSuccess` branches on `deleted`, and the caller must do the
 * same rather than treating a resolved mutation as a completed delete. The
 * local row SURVIVES a refusal (`localRecordDeleted: false`), so the list is
 * still correct — the certificate is still there because it is still working.
 *
 * ⚠️ NOTHING IS PRE-CHECKED. Flexi does not consult local state before calling.
 * Local state goes stale, a check would need its own network call to be worth
 * anything, it could disagree with the provider if a distribution is attached
 * in between, and it would make this code the authority on a rule the provider
 * owns. This follows the `deleteDnsZone` precedent exactly.
 */
export const useDeleteCertificateMutation = () => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: DeleteCertificateInput): Promise<DeleteCertificateResult | undefined> =>
            CertificatesService.removeCertificate(input),
        onSuccess: (result) => {

            // No result at all. Claims nothing — the delete may have landed.
            if (!result) {
                showSuccessNotification(
                    I18n.get('The API did not report the outcome of this deletion. Reload the list to see the current state.'),
                );
                queryClient.invalidateQueries({queryKey: certificatesKeys.lists()});
                return;
            }

            // ⚠️ THE REFUSAL PATH, REACHED THROUGH onSuccess. This is the case
            // the whole `DeleteCertificateResult` type exists for.
            if (!result.deleted) {
                const copy = deleteRefusedCopy(result);
                const detail = result.reason
                    ? `${copy.action} — ${result.reason}`
                    : copy.action;
                showErrorNotification(`${copy.title}. ${detail}`);
                // ⚠️ STILL INVALIDATED. The refusal is fresh information about
                // a certificate that is in use, and the list should reflect
                // whatever the provider says about it now.
                queryClient.invalidateQueries({queryKey: certificatesKeys.lists()});
                return;
            }

            // A genuine deletion. `localRecordDeleted` is reported when it
            // disagrees, because a certificate deleted at the provider whose
            // local row survived will keep appearing in the list until someone
            // notices — and silently is the worst way for that to happen.
            if (!result.localRecordDeleted) {
                showSuccessNotification(
                    I18n.get('The certificate was deleted at the provider, but its entry here was not removed. It may still appear in the list until that is cleaned up.'),
                );
            } else {
                showSuccessNotification(I18n.get('Certificate deleted'));
            }

            queryClient.invalidateQueries({queryKey: certificatesKeys.lists()});
        },
        // Reached only by a genuine transport or authorisation failure — an
        // in-use refusal never arrives here.
        onError: (e: unknown) => {
            notifyRequestError(e);
        },
    });
};
